import React, {forwardRef, useEffect, useImperativeHandle, useRef, useState} from "react";

type ProgressSliderProps = {
    onChange: (progress: number, isSliding?: boolean) => void;
    id?: string;
    min?: number;
    max?: number;
    modifyOnScroll?: boolean;
}

export type ProgressSliderApi = {
    setProgress: (progress: number) => void;
    isSliding: () => boolean;
    setMax: (max: number) => void;
};


const ProgressSliderView = forwardRef<ProgressSliderApi, ProgressSliderProps>((props, ref) => {

    const [progress, setProgress] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);
    const sliding = useRef(false);
    const [max, setMax] = useState(props.max ?? 100);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.style.setProperty('--progress', `${(progress / max) * 100}%`);
        }
    }, [progress, inputRef.current]);

    useEffect(() => {
        const handleOnScroll = (e: WheelEvent) => {
            if (!props.modifyOnScroll) return;

            e.preventDefault();
            const scrollAmount = max / 10; // Ajusta el factor de scroll
            let newProgress = progress;

            if (e.deltaY > 0) {
                newProgress = Math.max(progress - scrollAmount, props.min ?? 0);
            } else if (e.deltaY < 0) {
                newProgress = Math.min(progress + scrollAmount, max);
            }

            setProgress(newProgress);
            props.onChange(newProgress, sliding.current);
        };

        if (inputRef.current) {
            inputRef.current.addEventListener('wheel', handleOnScroll, {passive: false});
        }

        return () => {
            if (inputRef.current) {
                inputRef.current.removeEventListener('wheel', handleOnScroll);
            }
        };
    }, [progress, props.modifyOnScroll, props.min, max]);

    useImperativeHandle(ref, () => ({
        setProgress: setProgress,
        isSliding: () => sliding.current,
        setMax: (max: number) => setMax(max),
    }));

    const handleChange = (value: string) => {
        const newProgress = parseFloat(value);
        setProgress(newProgress);
        props.onChange(newProgress, sliding.current);
    }

    const handlePointerUp = (e: any) => {
        sliding.current = false;
        props.onChange(parseFloat((e.target as any as HTMLInputElement).value), sliding.current);
    }

    return (
        <input type="range" className="form-range time-progress-slider" id={props.id ?? "timeSlider"}
               min={props.min ?? 0} max={max}
               value={progress}
               ref={inputRef}
               onPointerDown={(e) => {
                   sliding.current = true
               }}
               onMouseUp={handlePointerUp}
               onTouchEnd={handlePointerUp}
               onChange={e => handleChange(e.target.value)}/>
    );
});

export default ProgressSliderView;
